/**
 * Le informa al Service Worker en qué contexto corre esta ventana
 * @module utils/swClientContext
 * @version 6.11.41.00
 *
 * Por qué existe: el Service Worker es uno solo por origen. En Android la PWA
 * instalada y las pestañas de Chrome comparten registro, así que cuando llega el
 * click de una notificación `clients.matchAll()` devuelve las dos cosas
 * mezcladas y no hay ningún campo del `Client` que las distinga. Sin este dato,
 * el SW enfocaba la primera ventana que encontrara —muchas veces una pestaña
 * vieja del navegador— y el usuario terminaba en la pantalla "Instalación
 * Requerida" en vez de en la app.
 *
 * Dos vías, porque ninguna alcanza sola:
 *  - PUSH: se anuncia al arrancar y cuando cambia el display-mode. Barato, pero
 *    el SW se apaga entre eventos y pierde lo que guardó en memoria.
 *  - PULL: el SW pregunta `ASK_CLIENT_CONTEXT` en el momento del click y se
 *    responde por el `MessagePort` que manda. Siempre al día, pero requiere que
 *    la página esté viva (una pestaña congelada no contesta → el SW hace
 *    timeout y sigue con `openWindow()`).
 */

import { getDeviceInfo, isMobileDevice } from './deviceUtils';

export interface SwClientContext {
  /** Corriendo como PWA instalada (standalone) */
  standalone: boolean;
  /** Dispositivo móvil (en desktop no hay app que abrir, reusar la pestaña es correcto) */
  mobile: boolean;
}

const buildContext = (): SwClientContext => ({
  standalone: getDeviceInfo().isStandalone,
  mobile: isMobileDevice()
});

const announceContext = (): void => {
  // Solo el SW que controla esta página puede recibir el postMessage. Si todavía
  // no controla (primera visita antes del claim), no pasa nada: el SW pregunta
  // en vivo cuando lo necesita.
  navigator.serviceWorker?.controller?.postMessage({
    type: 'CLIENT_CONTEXT',
    ...buildContext()
  });
};

/**
 * Registra el canal de respuesta y anuncia el contexto inicial.
 * Idempotente por si se llama más de una vez.
 */
let initialized = false;

export const initSwClientContext = (): void => {
  if (initialized || !('serviceWorker' in navigator)) return;
  initialized = true;

  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type !== 'ASK_CLIENT_CONTEXT') return;
    event.ports?.[0]?.postMessage(buildContext());
  });

  announceContext();
  navigator.serviceWorker.ready.then(announceContext).catch(() => { /* sin SW: nada que anunciar */ });
  navigator.serviceWorker.addEventListener('controllerchange', announceContext);

  // El display-mode puede cambiar en caliente (instalar la app desde la pestaña
  // abierta, o abrirla desde el ícono con la pestaña viva).
  try {
    window.matchMedia('(display-mode: standalone)').addEventListener('change', announceContext);
  } catch {
    // Safari viejo: sin addEventListener en MediaQueryList. Se pierde el cambio
    // en caliente, pero el anuncio del próximo arranque lo corrige.
  }
};
